import type { AlertItem, PiiEntityInAlert } from "@/lib/queries/alerts";
import { PII_BADGE_CLASSES } from "@/lib/pii-badge";

export const ALERT_STATE_OPTIONS = [
  "All",
  "Open",
  "Assigned",
  "Resolved",
  "Ignored",
] as const;

export const ALERT_TYPE_OPTIONS = [
  "all",
  "pii_exposure",
  "ai_content_detected",
  "voice_authentication_failure",
] as const;

export const ALERT_DATE_FILTERS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Past 24 hours" },
  { value: "week", label: "Past Week" },
  { value: "month", label: "Past Month" },
  { value: "year", label: "Past Year" },
] as const;

export type AlertStateFilter = (typeof ALERT_STATE_OPTIONS)[number];
export type AlertTypeFilter = (typeof ALERT_TYPE_OPTIONS)[number];
export type AlertDateFilter = (typeof ALERT_DATE_FILTERS)[number]["value"];

export function getAlertDateRange(filter: string | undefined): {
  start_date?: string;
  end_date?: string;
} {
  if (!filter || filter === "all") return {};

  const now = new Date();
  // Snap to the start of the current minute so the computed range stays stable
  // across re-renders (otherwise the value changes every millisecond and the
  // query key would change on every render, causing endless refetching).
  now.setSeconds(0, 0);
  const start = new Date(now);

  switch (filter) {
    case "today":
      start.setDate(now.getDate() - 1);
      break;
    case "week":
      start.setDate(now.getDate() - 7);
      break;
    case "month":
      start.setMonth(now.getMonth() - 1);
      break;
    case "year":
      start.setFullYear(now.getFullYear() - 1);
      break;
    default:
      return {};
  }

  return { start_date: start.toISOString(), end_date: now.toISOString() };
}

export function matchesAlertState(
  alert: AlertItem,
  stateFilters: Array<AlertStateFilter>,
): boolean {
  if (stateFilters.length === 0 || stateFilters.includes("All")) return true;
  return stateFilters.includes(alert.state as AlertStateFilter);
}

export function matchesAlertType(
  alert: AlertItem,
  typeFilters: Array<AlertTypeFilter>,
): boolean {
  if (typeFilters.length === 0 || typeFilters.includes("all")) return true;
  const alertTypes = alert.alert_types ?? [];
  return typeFilters.some((filter) => alertTypes.includes(filter));
}

export type AlertThreat = {
  label: string;
  tone: "critical" | "warning";
  kind: "ai" | "pii" | "voice";
};

const AVATAR_COLORS = [
  "bg-pink-100 text-pink-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-blue-100 text-blue-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-green-100 text-green-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-orange-100 text-orange-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
  "bg-purple-100 text-purple-700 dark:bg-[#22262F] dark:text-[#CECFD2]",
];

export function formatAlertType(alertType: string): string {
  const typeMap: Record<string, string> = {
    pii_exposure: "PII Exposure",
    ai_content_detected: "AI Content Detected",
    voice_authentication_failure: "Voice Authentication Failure",
  };
  return typeMap[alertType] || alertType;
}

export function formatAlertTypeShort(alertType: string): string {
  const typeMap: Record<string, string> = {
    pii_exposure: "PII Exposure",
    ai_content_detected: "AI Content",
    voice_authentication_failure: "Voice Authentication",
  };
  return typeMap[alertType] || alertType;
}

export function formatAlertTypeBadge(alertType: string): string {
  const typeMap: Record<string, string> = {
    pii_exposure: "PII Leak",
    ai_content_detected: "AI Content",
    voice_authentication_failure: "Deepfake",
  };
  return typeMap[alertType] || alertType;
}

export function getAlertTypeBadgeClasses(alertType: string): string {
  switch (alertType) {
    case "voice_authentication_failure":
      return "border-[#FECDCA] bg-[#FEF3F2] text-[#D92D20] dark:border-[#912018] dark:bg-[#55160C] dark:text-[#F97066]";
    case "ai_content_detected":
      return "border-[#F9DBAF] bg-[#FEF6EE] text-[#B93815] dark:border-[#932F19] dark:bg-[#551C10] dark:text-[#EF6820]";
    case "pii_exposure":
      return PII_BADGE_CLASSES;
    default:
      return "border-[#E9EAEB] bg-[#FAFAFA] text-[#414651] dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2]";
  }
}

export function formatAlertDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function getAlertThreats(
  alert: Pick<
    AlertItem,
    "ai_content_score" | "pii_entities" | "voice_auth_score"
  >,
): Array<AlertThreat> {
  const threats: Array<AlertThreat> = [];

  const aiScore = alert.ai_content_score?.ai_content_score;
  if (aiScore != null) {
    const aiPercentage = Math.round(aiScore * 100);
    if (aiPercentage > 0) {
      threats.push({
        label: `${aiPercentage}% AI`,
        tone: aiPercentage >= 50 ? "critical" : "warning",
        kind: "ai",
      });
    }
  }

  const piiCount = alert.pii_entities?.length ?? 0;
  if (piiCount > 0) {
    threats.push({
      label: `${piiCount} PII`,
      tone: piiCount >= 10 ? "critical" : "warning",
      kind: "pii",
    });
  }

  const voiceScore = alert.voice_auth_score?.voice_auth_score;
  if (voiceScore != null) {
    const voicePercentage = Math.round(voiceScore * 100);
    if (voicePercentage > 0) {
      threats.push({
        label: `${voicePercentage}%`,
        tone: voicePercentage >= 50 ? "critical" : "warning",
        kind: "voice",
      });
    }
  }

  return threats;
}

export function getAlertEntities(alert: {
  pii_entities: Array<PiiEntityInAlert> | null;
}): Array<string> {
  if (!Array.isArray(alert.pii_entities)) return [];
  const unique = new Set<string>();
  for (const entity of alert.pii_entities) {
    if (entity.entity_type) unique.add(entity.entity_type);
  }
  return Array.from(unique);
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}
